/**
 * Sidebar component — collapsible navigation with localStorage persistence
 * (W1 S0b extraction from App.tsx, moved verbatim).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, X, PanelLeftClose, PanelLeft, Users, ExternalLink, HelpCircle, UserCircle } from 'lucide-react';
import { VersionBadge } from '@secureembed/shared-crm';
import { getNavigationContext } from './nav';
import ApprovedNavigation from '../../navigation/ApprovedNavigation';
import { activeNavigationId, destinationMatches } from '../../navigation/routeBindings';
import { navigationTrail } from '../../navigation/model';
import { isW1Shell } from '../../utils/w1Flag';
import OpenExternalPortal from './OpenExternalPortal';

const Sidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onOpenHelp?: () => void;
}> = ({ isOpen, onClose, isCollapsed, onToggleCollapse, onOpenHelp }) => {
  const location = useLocation();
  const flagOn = isW1Shell();
  const compact = isCollapsed && !isOpen;

  const context = getNavigationContext(location.pathname, location.search);
  const activeId = activeNavigationId(context);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set(activeId ? navigationTrail(activeId).slice(0, -1).map(node => node.id) : []));
  const asideRef = useRef<HTMLElement>(null);
  const mobileTrigger = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (activeId) setExpandedIds(previous => new Set([...previous, ...navigationTrail(activeId).slice(0, -1).map(node => node.id)]));
  }, [activeId]);
  useEffect(() => {
    if (!isOpen) return;
    mobileTrigger.current = document.activeElement as HTMLElement;
    asideRef.current?.querySelector<HTMLButtonElement>('button[aria-label="Close navigation"]')?.focus();
    return () => { mobileTrigger.current?.focus(); };
  }, [isOpen]);
  const isPathActive = (href: string) => destinationMatches(href, location.pathname, location.search);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside ref={asideRef} role={isOpen ? 'dialog' : undefined} aria-modal={isOpen ? true : undefined} aria-label="Application sidebar" onKeyDown={event => {
        if (!isOpen) return;
        if (event.key === 'Escape') { event.preventDefault(); onClose(); }
        if (event.key === 'Tab') {
          const elements = [...(asideRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [])].filter(element => element.getClientRects().length > 0);
          const first = elements[0], last = elements[elements.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      }} className={`
        fixed top-0 left-0 z-50 h-screen min-h-screen bg-iflow-700 text-white transform transition-all duration-200
        lg:translate-x-0 lg:static lg:z-0 lg:h-auto lg:min-h-full flex flex-col
        ${isOpen ? 'visible translate-x-0' : 'invisible lg:visible -translate-x-full'}
        ${compact ? 'w-16' : 'w-64'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <div className="flex items-start space-x-2">
            <Shield className="w-8 h-8 flex-shrink-0" />
            {!compact && (
              <div className="flex flex-col leading-tight mt-0.5">
                <span className="font-bold text-lg leading-tight">SecureEmbed</span>
                <span className="text-[10px] text-white/50 uppercase tracking-wider">Platform Management</span>
                <VersionBadge portal="dashboard" collapsed={false} />
              </div>
            )}
          </div>
          <button type="button" aria-label="Close navigation" onClick={onClose} className="lg:hidden">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Collapse toggle button */}
        <div className="hidden lg:block px-3 py-2">
          <button
            onClick={onToggleCollapse}
            className={`w-full flex items-center px-3 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all ${compact ? 'justify-center' : ''}`}
            title={compact ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {compact ? (
              <PanelLeft className="w-5 h-5" />
            ) : (
              <PanelLeftClose className="w-5 h-5" />
            )}
          </button>
        </div>

        <ApprovedNavigation context={context} expandedIds={expandedIds} onExpandedChange={setExpandedIds}
          collapsed={compact} onExpandSidebar={onToggleCollapse} onNavigate={onClose} />

        {/* External portal link. Flag OFF: original single hard-coded
            CloudFront link, byte-for-byte unchanged. Flag ON: G09's
            allow-listed, confirmed navigation (OpenExternalPortal) —
            replaces this exact hard-coded-URL gap the contract names. */}
        {!flagOn && !compact && (
          <div className="flex-shrink-0 px-2 pb-2">
            <div className="border-t border-white/10 pt-2">
              <a
                href="https://d2k5ne4hoaiwmb.cloudfront.net"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors text-sm"
              >
                <Users className="w-4 h-4 flex-shrink-0" />
                <span className="truncate ml-3">Stakeholder Portal</span>
                <ExternalLink className="w-3.5 h-3.5 ml-auto flex-shrink-0" />
              </a>
            </div>
          </div>
        )}
        {!flagOn && compact && (
          <div className="flex-shrink-0 px-2 pb-2">
            <div className="border-t border-white/10 pt-2">
              <a
                href="https://d2k5ne4hoaiwmb.cloudfront.net"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                title="Stakeholder Portal"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </div>
        )}
        {flagOn && (
          <div className="flex-shrink-0 px-2 pb-2">
            <div className="border-t border-white/10 pt-2">
              <OpenExternalPortal collapsed={compact} />
            </div>
          </div>
        )}

        {/* G01 sidebar footer: Help + My account (Bug #5625). Help opens the
            same HelpDrawer as Header's G07 trigger — onOpenHelp is threaded
            from Layout, which owns the drawer's open state. My account links
            to the existing /my-account/profile route. */}
        {flagOn && (
          <div className="flex-shrink-0 px-2 pb-3">
            <div className="border-t border-white/10 pt-2">
              <button
                type="button"
                onClick={onOpenHelp}
                title={compact ? 'Help' : undefined}
                aria-label={compact ? 'Help' : undefined}
                className={`w-full flex items-center px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors ${compact ? 'justify-center' : ''}`}
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                {!compact && <span className="truncate ml-3 text-sm">Help</span>}
              </button>
              <Link
                to="/my-account/profile"
                onClick={onClose}
                title={compact ? 'My account' : undefined}
                aria-label={compact ? 'My account' : undefined}
                aria-current={isPathActive('/my-account/profile') ? 'page' : undefined}
                className={`flex items-center px-3 py-2 rounded-lg transition-colors ${compact ? 'justify-center' : ''} ${
                  isPathActive('/my-account/profile')
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <UserCircle className="w-5 h-5 flex-shrink-0" />
                {!compact && <span className="truncate ml-3 text-sm">My account</span>}
              </Link>
            </div>
          </div>
        )}

        <div className="flex-shrink-0 px-2 pb-2">
          <Link to="/my-account/preferences" onClick={onClose} title="My preferences" aria-label="My preferences" className="block rounded px-3 py-2 text-sm text-white/70 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">{compact ? <UserCircle className="h-5 w-5" aria-hidden="true" /> : 'My preferences'}</Link>
        </div>
        <div className="flex-shrink-0 h-4" />
      </aside>
    </>
  );
};

export default Sidebar;
